import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useIsAllowed } from '../../utils/useCurrentUser';
import { ConfirmModal } from '../modal/confirm-modal/ConfirmModal.component';
import { useState, useEffect } from 'react';

/**
 * PageGuard component - Guards entire pages based on user roles
 * Shows ConfirmModal if user doesn't have access
 *
 * @param {Object} props
 * @param {Array<string>} props.allowedRoles - Array of role strings required to access the page
 * @param {React.ReactNode} props.children - Page content to display if authorized
 * @param {string} props.redirectTo - Path to redirect to when user denies access (default: -1 for back)
 *
 * @example
 * <PageGuard allowedRoles={[REVENUE_SUB_ROLES.ONLINE_REVENUE_APPLICATION]}>
 *   <YourPageContent />
 * </PageGuard>
 */
export default function PageGuard({
  children,
  allowedRoles = [],
  redirectTo = -1,
}) {
  const history = useHistory();
  const { isAllowed, isLoadingUser } = useIsAllowed();
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  // Show access denied modal once page loads and user is not allowed
  useEffect(() => {
    if (!isLoadingUser && allowedRoles.length > 0) {
      const hasAccess = isAllowed(allowedRoles);
      if (!hasAccess) {
        setShowAccessDenied(true);
      }
    }
  }, [isLoadingUser, allowedRoles, isAllowed]);

  const handleAccessDenied = () => {
    if (typeof redirectTo === 'number') {
      history.go(redirectTo); // navigate(-1) for back, etc.
    } else {
      history.push(redirectTo); // navigate to specific path
    }
  };

  // Still loading, show nothing
  if (isLoadingUser) {
    return null;
  }

  // No roles required or user is allowed
  if (allowedRoles.length === 0 || isAllowed(allowedRoles)) {
    return <>{children}</>;
  }

  // User denied access - show modal
  return (
    <ConfirmModal
      isOpen={showAccessDenied}
      closeModal={handleAccessDenied}
      title="Access Denied"
      description="You do not have permission to access this page. Please contact your administrator if you believe this is an error."
      secondaryActionText="Go Back"
      actionBtnClasses="secondary"
    />
  );
}
